import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { superAdminPlayerService } from '../../services/superAdminPlayerService';
import { schoolService } from '../../services/schoolService';
import PlayerSearchResults from './components/PlayerSearchResults';
import PlayerDetailEditor from './components/PlayerDetailEditor';
import { useAuth } from '../../contexts/AuthContext';

const SuperAdminPlayerEditor = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    city: 'all',
    ageCategory: 'all',
    schoolId: 'all'
  });

  // Results state
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 1
  });

  // Options for filters
  const [cityOptions, setCityOptions] = useState([
    { value: 'all', label: 'All Cities' },
    { value: 'mumbai', label: 'Mumbai' },
    { value: 'nagpur', label: 'Nagpur' }
  ]);

  const [ageCategoryOptions, setAgeCategoryOptions] = useState([
    { value: 'all', label: 'All Categories' },
    { value: 'boys_u14', label: 'Boys U14' },
    { value: 'girls_u15', label: 'Girls U15' },
    { value: 'boys_u16', label: 'Boys U16' }
  ]);

  const [schoolOptions, setSchoolOptions] = useState([
    { value: 'all', label: 'All Schools' }
  ]);

  useEffect(() => {
    const storedRole = localStorage.getItem('userRole');
    if (storedRole) {
      setUserRole(storedRole);
      if (storedRole !== 'super_admin') {
        navigate('/admin-dashboard');
        return;
      }
    } else {
      navigate('/admin-login');
      return;
    }

    loadSchools();
  }, [navigate]);

  useEffect(() => {
    if (searchTerm || filters.city !== 'all' || filters.ageCategory !== 'all' || filters.schoolId !== 'all') {
      handleSearch();
    }
  }, [filters, pagination.page]);

  const loadSchools = async () => {
    try {
      const schools = await schoolService.getAllSchools(null);
      const options = [
        { value: 'all', label: 'All Schools' },
        ...(schools || []).map(school => ({
          value: school.id,
          label: school.name
        }))
      ];
      setSchoolOptions(options);
    } catch (error) {
      console.error('Error loading schools:', error);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError('');
      setShowEditor(false);
      setSelectedPlayer(null);

      const searchFilters = {
        searchTerm: searchTerm.trim() || null,
        city: filters.city !== 'all' ? filters.city : null,
        ageCategory: filters.ageCategory !== 'all' ? filters.ageCategory : null,
        schoolId: filters.schoolId !== 'all' ? filters.schoolId : null
      };

      const result = await superAdminPlayerService.searchPlayers(
        searchFilters,
        { page: pagination.page, pageSize: pagination.pageSize }
      );

      setPlayers(result.data || []);
      setPagination(prev => ({
        ...prev,
        total: result.total || 0,
        totalPages: result.totalPages || 1
      }));
    } catch (err) {
      console.error('Error searching players:', err);
      setError(err?.message || 'Failed to search players');
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerSelect = async (playerId) => {
    try {
      setLoading(true);
      const details = await superAdminPlayerService.getPlayerDetails(playerId);
      setSelectedPlayer(details);
      setShowEditor(true);
    } catch (err) {
      console.error('Error loading player details:', err);
      setError(err?.message || 'Failed to load player details');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerUpdate = async (updatedPlayer) => {
    try {
      setLoading(true);
      await superAdminPlayerService.updatePlayer(updatedPlayer.id, updatedPlayer);
      
      // Refresh search results
      await handleSearch();
      
      // Reload player details
      const details = await superAdminPlayerService.getPlayerDetails(updatedPlayer.id);
      setSelectedPlayer(details);
      
      alert('Player updated successfully!');
    } catch (err) {
      console.error('Error updating player:', err);
      setError(err?.message || 'Failed to update player');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setSelectedPlayer(null);
  };

  return (
    <>
      <AdminSidebarNavigation
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div className={`main-content with-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Super Admin - Player Editor
            </h1>
            <p className="text-gray-600">
              Search and update player details and documents without affecting registration status
            </p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {!showEditor ? (
            <div className="space-y-6">
              {/* Search and Filters */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Input
                      label="Search"
                      placeholder="Name, MCA ID, or School..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Select
                      label="City"
                      options={cityOptions}
                      value={filters.city}
                      onChange={(value) => setFilters(prev => ({ ...prev, city: value }))}
                    />
                    <Select
                      label="Age Category"
                      options={ageCategoryOptions}
                      value={filters.ageCategory}
                      onChange={(value) => setFilters(prev => ({ ...prev, ageCategory: value }))}
                    />
                    <Select
                      label="School"
                      options={schoolOptions}
                      value={filters.schoolId}
                      onChange={(value) => setFilters(prev => ({ ...prev, schoolId: value }))}
                      searchable
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSearch} disabled={loading}>
                      <Icon name="Search" size={16} className="mr-2" />
                      Search
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchTerm('');
                        setFilters({ city: 'all', ageCategory: 'all', schoolId: 'all' });
                        setPlayers([]);
                        setPagination(prev => ({ ...prev, page: 1 }));
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </div>

              {/* Results */}
              <PlayerSearchResults
                players={players}
                loading={loading}
                pagination={pagination}
                onPlayerSelect={handlePlayerSelect}
                onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
              />
            </div>
          ) : (
            <PlayerDetailEditor
              playerData={selectedPlayer}
              onSave={handlePlayerUpdate}
              onClose={handleCloseEditor}
              loading={loading}
              onPlayerDataUpdate={(updated) => setSelectedPlayer(updated)}
            />
          )}
      </div>
    </>
  );
};

export default SuperAdminPlayerEditor;

